import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import { auth, analytics } from './firebase/firebase';
import { onAuthStateChanged, signOut, getIdTokenResult } from 'firebase/auth';
import { logEvent } from 'firebase/analytics';
import Auth from './firebase/Auth';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const upgradeParam = new URLSearchParams(window.location.search).get("upgrade");
if (upgradeParam === "true") {
  localStorage.setItem("upgradeIntent", "true");
}

function App() {
  const [pastSales, setPastSales] = useState('');
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [isPaidUser, setIsPaidUser] = useState(null);

  const handleUpgrade = useCallback(async () => {
    if (!user || isPaidUser) {
      console.log("❌ Skipping Stripe – not logged in or already paid");
      return;
    }

    try {
      logEvent(analytics, 'begin_checkout', { email: user.email });
      const response = await fetch("https://api.supply.ai.luxestrive.com/create-checkout-session", {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email }),
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        console.error("❌ Stripe session creation failed");
      }
    } catch (err) {
      console.error("Stripe checkout error:", err);
    }
  }, [user, isPaidUser]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);

      if (currentUser) {
        try {
          const token = await getIdTokenResult(currentUser, true);
          const paid = !!token.claims.paidUser;
          setIsPaidUser(paid);
          localStorage.setItem("paidUser", paid);
          localStorage.setItem("email", currentUser.email);

          if (localStorage.getItem("upgradeIntent") === "true") {
            const justPaid = new URLSearchParams(window.location.search).get("paid") === "true";
            localStorage.removeItem("upgradeIntent");

            if (paid || justPaid) {
              console.log("✅ Already paid. Skipping Stripe.");
              window.history.replaceState({}, document.title, window.location.pathname);
            } else {
              console.log("🚀 Triggering Stripe...");
              handleUpgrade();
            }
          }
        } catch (err) {
          console.error("Token claim error:", err);
          setIsPaidUser(false);
        }
      } else {
        setIsPaidUser(false);
        localStorage.removeItem("paidUser");
        localStorage.removeItem("email");
      }
    });

    return () => unsubscribe();
  }, [handleUpgrade]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paid = params.get("paid");

    if (paid && user) {
      console.log("🔄 Refreshing token after Stripe payment...");
      user.getIdToken(true).then(() => {
        logEvent(analytics, 'purchase', {
          value: 1.0,
          currency: 'USD',
          email: user.email,
        });
        window.location.href = window.location.pathname;
      });
    }
  }, [user]);

  const handleManageBilling = async () => {
    const response = await fetch("https://api.supply.ai.luxestrive.com/create-portal-session", {
      method: "POST",
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: user.email }),
    });

    const data = await response.json();
    if (data.url) {
      window.location.href = data.url;
    } else {
      console.error("❌ Billing portal failed to load.");
    }
  };

  const handlePredict = async () => {
    setLoading(true);
    setError(null);

    const salesArray = pastSales
      .split(',')
      .map(num => num.trim())
      .filter(num => num !== '' && !isNaN(num))
      .map(Number);

    if (salesArray.length === 0) {
      setError(
        <span><span role="img" aria-label="warning">⚠️</span> Please enter valid sales numbers separated by commas.</span>
      );
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('https://api.supply.ai.luxestrive.com/predict', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ past_sales: salesArray }),
      });

      if (!response.ok) throw new Error("Prediction failed");

      const data = await response.json();
      setPrediction(data);
    } catch (error) {
      console.error("Prediction error:", error);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="auth-container">
        <div className="auth-box">
          <Auth onAuthSuccess={() => { }} />
        </div>
      </div>
    );
  }

  const firstName = (user?.displayName || '').trim().split(' ')[0];

  return (
    <div className="container">
      <h1>AI-Powered Demand Forecasting</h1>
      {firstName && <h3 style={{ marginBottom: '10px' }}><span role="img" aria-label="wave">👋</span> Hi, {firstName}!</h3>}

      {isPaidUser && (
        <div className="plan-banner" style={{ padding: '15px', backgroundColor: '#d4edda', border: '1px solid #28a745', color: '#155724', borderRadius: '6px', marginBottom: '15px', textAlign: 'center' }}>
          <span role="img" aria-label="check">✅</span> You already have an active subscription
          <br />
          <button onClick={handleManageBilling} style={{ marginTop: '10px', padding: '8px 16px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Manage Billing</button>
        </div>
      )}

      {!isPaidUser ? (
        <div className="locked-banner">
          <h2><span role="img" aria-label="lock">🔐</span> Full Access Locked</h2>
          <p>You must upgrade to unlock AI-powered forecasting features.</p>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button onClick={handleUpgrade}>Upgrade Now</button>
            <button onClick={() => signOut(auth)}>Logout</button>
          </div>
        </div>
      ) : (
        <div className="form-container">
          <p className="helper-text">
            <span role="img" aria-label="bar chart">📊</span> Enter your historical sales data as comma-separated numbers (e.g., 100, 200, 150)
          </p>
          <input type="text" placeholder="100, 200, 150, 300" value={pastSales} onChange={e => setPastSales(e.target.value)} />
          {error && <p className="error-message">{error}</p>}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button onClick={handlePredict} disabled={loading}>{loading ? 'Predicting...' : 'Predict Demand'}</button>
            <button onClick={() => signOut(auth)}>Logout</button>
          </div>

          {prediction && (
            <div>
              <h2>Predicted Demand</h2>
              <p><strong>Model:</strong> {prediction.model}</p>
              <p><strong>Timestamp:</strong> {prediction.timestamp}</p>
              <div className="table-container">
                <table>
                  <thead><tr><th>Day</th><th>Predicted Sales</th></tr></thead>
                  <tbody>
                    {prediction.predictions.slice(0, showAll ? prediction.predictions.length : 10).map((val, i) => (
                      <tr key={i}>
                        <td>Day {i + 1}</td>
                        <td className={val > 500 ? 'high-prediction' : 'predicted-value'}>{val}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {prediction.predictions.length > 10 && (
                <button onClick={() => setShowAll(!showAll)}>
                  {showAll ? 'Show Less' : 'Show More'}
                </button>
              )}
              <h3>Sales Prediction Chart</h3>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={prediction.predictions.map((val, i) => ({ day: i + 1, sales: val }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="sales" stroke="#8884d8" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}

      <footer className="footer">
        <p>Powered by <strong>Luxestrive AI</strong></p>
      </footer>
    </div>
  );
}

export default App; // This is end of line
